import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import config from '../../config/index';
import Calendar from '../Calendar';
import { showToast, convertISOFormatToDate } from '../../lib/tools';
import '../../styles/react-day-picker-style.css';
import AppButton from '../../styles/common/AppButton';
import Search from '../Icons/Search';
import { FULLSCREEN_MODE } from '../../lib/constants';
import ColorSF2 from '../../styles/common/ColorSF2';
import {
  TagSelect,
  Greater,
  SearchAreaWrapper,
  SearchArea,
  SearchOptionsTable,
  InputTitle,
  DateTimeTd,
  InputDatetimeWrapper,
  InputText,
  CheckboxContents,
  CheckboxLabel,
  RadioLabel,
  RadioGroup,
  TagSearchWrapper,
  ItemTagWrapper,
  CheckboxWrapper,
  Checkbox,
  DefaultCheckbox,
  CheckboxTitle,
  ButtonWrapper,
  SaveButton,
} from './styles/TimeLineSearchBoxStyles';
import saveSearchCriteria from './functions/TimeLineSearchBoxUtils';

const createOptions = value => (
  <option value={value.id} key={value.id}>
    {value.name}
  </option>
);

/**
 * 指定された階層のタグ選択ボックスを生成する。
 *
 * @param {Function} formatMessage - i18n対応メッセージをフォーマットする関数。
 * @param {Object} tags - 選択されている階層の次の階層までのタグ情報が格納されたオブジェクト。
 *例：階層1が選択されている時、tagsには階層2までのタグ情報が格納されている。
 * @param {Function} onSearch - タグが選択された時に呼ばれるコールバック関数。
 * @param {number[]} selectedTagIds - 選択されているタグのIDの配列。
 * @param {boolean} unSelectable - タグが選択不可能であるかどうかを示すフラグ。
 * @param {number} [maxDepth=5] - 生成するタグ選択ボックスの最大深さ（デフォルトは5）。
 * reduxで管理する`state.timeline.searchOptions.maxDepth`を参照している。
 * @returns {React.Fragment}
 */
const createTagSelectBox = (
  formatMessage,
  tags,
  onSearch,
  selectedTagIds,
  unSelectable,
  maxDepth,
) => {
  const maxDisplayTagDepth = maxDepth >= 3 ? 5 : 2;
  const depthArray = Array.from(
    { length: maxDisplayTagDepth },
    (_, i) => i + 1,
  );

  return (
    <Fragment key="TimeLineSearchBoxTag">
      {depthArray.map(depth => {
        const isTagDepthExceeded = Object.keys(tags).length < depth;
        const isPreviousTagNotSelected =
          depth !== 1 &&
          (selectedTagIds[depth - 2] === null ||
            selectedTagIds[depth - 2] === undefined);
        const items = tags[depth];
        const optionLabel = formatMessage(
          { id: 'timeLine.timeLineSearchBox.select.depth' },
          { depth },
        );
        const selected =
          selectedTagIds.length >= depth ? selectedTagIds[depth - 1] : -1;

        return (
          <Fragment key={`TimeLineSearchBoxTag${depth}`}>
            <TagSelect
              disabled={
                unSelectable || isTagDepthExceeded || isPreviousTagNotSelected
              }
              value={selected}
              className={selected && selected !== -1 && 'selected'}
              onChange={e =>
                onSearch({ tags: { depth, tagId: Number(e.target.value) } })
              }
            >
              <option value={-1}>{optionLabel}</option>
              {!isTagDepthExceeded &&
                items &&
                items.map(value => createOptions(value))}
            </TagSelect>
            {depth !== maxDisplayTagDepth && (
              <Greater>
                <FormattedMessage id="timeLine.timeLineSearchBox.title.greater" />
              </Greater>
            )}
          </Fragment>
        );
      })}
    </Fragment>
  );
};

/**
 * 階層タグ検索欄を生成するコンポーネント
 * @param {Object} props
 * @param {Object} props.searchOptions - 検索条件
 * @param {Object} props.searchOptionsError - 検索条件のエラー情報
 * @param {Object} tags - 選択されている階層の次の階層までの階層タグ情報が格納されたオブジェクト。
 *例：階層1が選択されている時、tagsには階層2までの階層タグ情報が格納されている。
 * @param {Array} props.selectedTagIds - 選択されている階層タグのIDの配列
 * @param {Function} props.onSearch - 検索条件が更新された時に呼ばれるコールバック関数
 * @param {boolean} props.isShowSaveSearchCriteria - 検索条件保存ボタンを表示するかどうかを示すフラグ
 * @param {Function} props.onCheck - 未設定タグのみを検索するチェックボックスがクリックされた時に呼ばれるコールバック関数
 * @param {boolean} props.isSearchForUnsetTagOnly - 未設定タグのみを検索するかどうかを示すフラグ
 * @param {Object} props.provider - プロバイダ情報
 * @param {Function} props.searchTags - タグ検索ボタンがクリックされた時に呼ばれるコールバック関数
 * @param {Function} props.onChangeDisplayTag - 表示するタグの種類を変更する時に呼ばれるコールバック関数
 * @param {Function} props.toggleTagSearchDialog - タグ検索ダイアログを表示するかどうかを示すフラグを切り替える時に呼ばれるコールバック関数
 * @returns
 */
const TimeLineSearchBox = ({
  searchOptions,
  searchOptionsError,
  tags,
  selectedTagIds,
  onSearch,
  isShowSaveSearchCriteria,
  onCheck,
  isSearchForUnsetTagOnly,
  provider,
  searchTags,
  onChangeDisplayTag,
  toggleTagSearchDialog,
}) => {
  const {
    recordedDateFrom,
    recordedDateTo,
    partialOperatorName,
    partialDeviceNickname,
    freeTag,
  } = searchOptions;
  const intl = useIntl();
  const { formatMessage } = intl;
  const fullScreenMode = useSelector(state => state.main.fullScreenMode);
  const isNotTimelineTile = fullScreenMode !== FULLSCREEN_MODE.timelineTile;

  const placeholder = formatMessage({
    id: 'timeLine.timeLineSearchBox.placeholder.partialOperatorName',
  });
  const placeholderDevice = formatMessage({
    id: 'timeLine.timeLineSearchBox.placeholder.partialDeviceNickname',
  });
  const placeholderFreeTag = formatMessage({
    id: 'timeLine.timeLineSearchBox.placeholder.freeTag',
  });

  const onClear = () => {
    if (!isSearchForUnsetTagOnly && selectedTagIds.length !== 0) {
      onSearch({ tags: { depth: 1, tagId: -1 } });
    }
  };
  const isEmptyTags = tags && Object.values(tags).length === 0;
  const maxDepth = useSelector(state => state.timeline.selectOptions.maxDepth);
  return (
    <SearchAreaWrapper id="TimeLinePageSearchBox">
      <SearchArea>
        <tbody>
          <tr>
            <td>
              <SearchOptionsTable fullScreenMode={fullScreenMode}>
                <tbody>
                  <tr>
                    <th>
                      <InputTitle>
                        <FormattedMessage id="timeLine.timeLineSearchBox.title.recordedDateFrom" />
                      </InputTitle>
                    </th>
                    <DateTimeTd colSpan="2" fullScreenMode={fullScreenMode}>
                      <InputDatetimeWrapper>
                        <Calendar
                          value={recordedDateFrom}
                          error={searchOptionsError.recordedDateFrom}
                          onSearch={date => {
                            onSearch({
                              recordedDateFrom: convertISOFormatToDate(date),
                            });
                          }}
                        />
                        <FormattedMessage id="timeLine.timeLineSearchBox.fullTilde" />
                        <Calendar
                          value={recordedDateTo}
                          error={searchOptionsError.recordedDateTo}
                          onSearch={date => {
                            onSearch({
                              recordedDateTo: convertISOFormatToDate(date),
                            });
                          }}
                        />
                      </InputDatetimeWrapper>
                    </DateTimeTd>
                    {isNotTimelineTile && [
                      <th key="th_operator_name">
                        <FormattedMessage id="timeLine.timeLineSearchBox.title.partialOperatorName" />
                      </th>,
                      <td key="td_operator_name">
                        <InputText
                          id="timeLineSearchBoxPartialOperatorName"
                          type="text"
                          placeholder={placeholder}
                          value={partialOperatorName}
                          onChange={e =>
                            onSearch({ partialOperatorName: e.target.value })
                          }
                        />
                      </td>,
                    ]}
                    <th>
                      <FormattedMessage id="timeLine.timeLineSearchBox.title.partialDeviceNickname" />
                    </th>
                    <td>
                      <InputText
                        id="timeLineSearchBoxPartialDeviceNickname"
                        type="text"
                        placeholder={placeholderDevice}
                        value={partialDeviceNickname}
                        onChange={e =>
                          onSearch({ partialDeviceNickname: e.target.value })
                        }
                      />
                    </td>
                  </tr>
                  {isNotTimelineTile && [
                    <tr key="tr_free_tag">
                      <th>
                        <FormattedMessage id="timeLine.timeLineSearchBox.title.freeTag" />
                      </th>
                      <td colSpan="5">
                        <InputText
                          id="timeLineSearchBoxFreeTag"
                          type="text"
                          placeholder={placeholderFreeTag}
                          value={freeTag}
                          onChange={e => onSearch({ freeTag: e.target.value })}
                        />
                      </td>
                    </tr>,
                    <tr key="tr_hierarchical_tag">
                      <th>
                        <FormattedMessage id="timeLine.timeLineSearchBox.title.hierarchicalTag" />
                      </th>
                      <td colSpan="5">
                        <CheckboxContents>
                          <RadioLabel>
                            <FormattedMessage id="timeLine.timeLineSearchBox.label.showTagSetting" />
                          </RadioLabel>
                          <RadioLabel>:</RadioLabel>
                          <RadioGroup>
                            <input
                              type="radio"
                              name="showTagSetting"
                              defaultChecked={!searchOptions.isDeleted}
                              onChange={() => {
                                onClear();
                                onChangeDisplayTag({
                                  displayTag: config.DISPLAY_TAG.use,
                                });
                              }}
                            />
                            <label>
                              <FormattedMessage id="timeLine.timeLineSearchBox.label.useTag" />
                            </label>
                            <input
                              type="radio"
                              name="showTagSetting"
                              defaultChecked={searchOptions.isDeleted}
                              onChange={() => {
                                onClear();
                                onChangeDisplayTag({
                                  displayTag: config.DISPLAY_TAG.delete,
                                });
                              }}
                            />
                            <label>
                              <FormattedMessage id="timeLine.timeLineSearchBox.label.deleteTag" />
                            </label>
                            <input
                              type="radio"
                              name="showTagSetting"
                              defaultChecked={searchOptions.isDeleted === null}
                              onChange={() => {
                                onClear();
                                onChangeDisplayTag({
                                  displayTag: config.DISPLAY_TAG.all,
                                });
                              }}
                            />
                            <label>
                              <FormattedMessage id="timeLine.timeLineSearchBox.label.allTag" />
                            </label>
                          </RadioGroup>
                        </CheckboxContents>
                        <TagSearchWrapper>
                          <AppButton
                            small
                            width="120px"
                            className="button"
                            id="searchTimelineTag"
                            role="presentation"
                            onClick={() => {
                              toggleTagSearchDialog();
                              searchTags();
                            }}
                          >
                            <Search color="white" width="20px" height="20px" />
                            <FormattedMessage id="timeLine.timeLineSearchBox.button.searchTag" />
                          </AppButton>
                          <AppButton
                            small
                            width="90px"
                            bgColor={ColorSF2.white}
                            color={ColorSF2.black}
                            borderColor={ColorSF2.mediumGray}
                            disabled={
                              isSearchForUnsetTagOnly ||
                              selectedTagIds.length === 0
                            }
                            className="button"
                            id="searchTimelineTag"
                            role="presentation"
                            onClick={() => onClear()}
                          >
                            <FormattedMessage
                              id="common.button.clear.target"
                              values={{
                                target: formatMessage({
                                  id: 'common.label.item.tag',
                                }),
                              }}
                            />
                          </AppButton>
                        </TagSearchWrapper>
                        <ItemTagWrapper>
                          {tags &&
                            createTagSelectBox(
                              formatMessage,
                              tags,
                              onSearch,
                              selectedTagIds,
                              isSearchForUnsetTagOnly,
                              maxDepth,
                            )}
                        </ItemTagWrapper>
                        <CheckboxContents>
                          <CheckboxWrapper
                            id="timelineUnsetTagOnlyCheckAreaHead"
                            role="presentation"
                            onClick={() =>
                              !isEmptyTags && onCheck(!isSearchForUnsetTagOnly)
                            }
                          >
                            <Checkbox
                              id="timelineUnsetTagOnlyCheckboxAreaHead"
                              role="presentation"
                              onClick={e => !isEmptyTags && e.stopPropagation()}
                            >
                              <DefaultCheckbox
                                id="timelineUnsetTagOnlyCheckboxHeader"
                                type="checkbox"
                                value=""
                                onChange={() =>
                                  !isEmptyTags &&
                                  onCheck(!isSearchForUnsetTagOnly)
                                }
                                checked={
                                  isSearchForUnsetTagOnly ? 'checked' : ''
                                }
                              />
                              <CheckboxLabel
                                className="timelineCheckboxLabel"
                                disabled={isEmptyTags}
                                htmlFor="timelineUnsetTagOnlyCheckboxHeader"
                              />
                            </Checkbox>
                            <CheckboxTitle disabled={isEmptyTags}>
                              <FormattedMessage id="timeLine.timeLineSearchBox.label.unsetTagOnly" />
                            </CheckboxTitle>
                          </CheckboxWrapper>
                        </CheckboxContents>
                      </td>
                    </tr>,
                  ]}
                </tbody>
              </SearchOptionsTable>
            </td>
            {provider &&
              !provider.isCansuke &&
              isNotTimelineTile &&
              isShowSaveSearchCriteria && (
                <td style={{ height: '100px' }}>
                  <ButtonWrapper>
                    <SaveButton
                      className="button"
                      id="saveSearchCriteriaClick"
                      role="presentation"
                      onClick={() => {
                        saveSearchCriteria(searchOptions, selectedTagIds);
                        showToast(
                          formatMessage({
                            id: 'timeLine.timeLineSearchBox.toast.saveSearchCriteria',
                          }),
                        );
                      }}
                    >
                      <FormattedMessage id="timeLine.timeLineSearchBox.button.saveSearchCriteria" />
                    </SaveButton>
                  </ButtonWrapper>
                </td>
              )}
          </tr>
        </tbody>
      </SearchArea>
    </SearchAreaWrapper>
  );
};

TimeLineSearchBox.propTypes = {
  searchOptions: PropTypes.object.isRequired,
  searchOptionsError: PropTypes.object.isRequired,
  tags: PropTypes.object.isRequired,
  selectedTagIds: PropTypes.array.isRequired,
  onSearch: PropTypes.func.isRequired,
  isShowSaveSearchCriteria: PropTypes.bool.isRequired,
  onCheck: PropTypes.func,
  isSearchForUnsetTagOnly: PropTypes.bool,
  provider: PropTypes.object.isRequired,
  searchTags: PropTypes.func,
  onChangeDisplayTag: PropTypes.func.isRequired,
  toggleTagSearchDialog: PropTypes.func.isRequired,
};

TimeLineSearchBox.defaultProps = {
  onCheck: () => {},
  isSearchForUnsetTagOnly: false,
  searchTags: () => {},
};

export default TimeLineSearchBox;
